<script setup lang="ts">
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { useI18n } from 'vue-i18n'
import { usePlatformBranding } from '@/composables/usePlatformBranding'
import { $api } from '@/utils/api'

import authV2ForgotPasswordIllustrationDark from '@images/pages/auth-v2-forgot-password-illustration-dark.png'
import authV2ForgotPasswordIllustrationLight from '@images/pages/auth-v2-forgot-password-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { brandName, logoUrl, authHeadline, authSubheadline, loadPlatformBranding, showPlatformLogoFallback } = usePlatformBranding()

const authThemeImg = useGenerateImageVariant(authV2ForgotPasswordIllustrationLight, authV2ForgotPasswordIllustrationDark)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
    // Raggiunta da un link email one-time (token+email in query): deve
    // restare sempre accessibile anche se il browser ha cookie di sessione
    // residui (validi o scaduti), altrimenti la guardia di unauthenticatedOnly
    // rimbalza chi apre il link a '/' e, con un token non valido, il visitatore
    // finisce sul login invece di vedere il form di reset.
    allowWithActiveSession: true,
  },
})

const loading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const form = ref({
  email: String(route.query.email || ''),
  token: String(route.query.token || ''),
  password: '',
  password_confirmation: '',
})

const pageHeadline = computed(() => authHeadline.value || t('Reset Password'))
const pageSubheadline = computed(() => authSubheadline.value || brandName.value)

const submitResetPassword = async () => {
  if (!form.value.email || !form.value.token || loading.value)
    return

  loading.value = true
  successMessage.value = ''
  errorMessage.value = ''

  try {
    const response = await $api<{ status?: string }>('/auth/reset-password', {
      method: 'POST',
      body: {
        token: form.value.token,
        email: form.value.email,
        password: form.value.password,
        password_confirmation: form.value.password_confirmation,
        tenant_id: route.query.tenant_id ? Number(route.query.tenant_id) : undefined,
        app_id: route.query.app_id ? Number(route.query.app_id) : undefined,
      },
    })

    successMessage.value = response?.status || t('Reset Password')
    setTimeout(() => {
      router.push({ name: 'login' })
    }, 1200)
  }
  catch (error: any) {
    const payload = error?.data || error?._data || {}
    const validationErrors = payload?.errors || {}

    errorMessage.value = validationErrors?.email?.[0]
      || validationErrors?.password?.[0]
      || payload?.message
      || payload?.status
      || t('auth.verification_email_resend_error')
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  loadPlatformBranding()
})
</script>

<template>
  <RouterLink to="/" class="auth-logo d-flex align-center gap-x-3">
    <img
      v-if="logoUrl"
      :src="logoUrl"
      :alt="brandName"
      style="display:block; max-width: 180px; max-height: 48px; width: auto; height: auto; object-fit: contain;"
    >
    <VNodeRenderer v-else-if="showPlatformLogoFallback" :nodes="themeConfig.app.logo" />
    <h1 class="auth-title">
      {{ brandName }}
    </h1>
  </RouterLink>

  <VRow no-gutters class="auth-wrapper bg-surface">
    <VCol md="8" class="d-none d-md-flex">
      <div class="position-relative bg-background w-100 me-0">
        <div class="d-flex align-center justify-center w-100 h-100" style="padding-inline: 150px;">
          <VImg max-width="468" :src="authThemeImg" class="auth-illustration mt-16 mb-2" />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol cols="12" md="4" class="d-flex align-center justify-center">
      <VCard flat :max-width="500" class="mt-12 mt-sm-0 pa-4">
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ pageHeadline }}
          </h4>
          <p class="mb-0">
            {{ pageSubheadline }}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submitResetPassword">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  :label="$t('auth.email')"
                  readonly
                  type="email"
                  autocomplete="off"
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :label="$t('auth.password')"
                  type="password"
                  autocomplete="new-password"
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="form.password_confirmation"
                  :label="$t('auth.confirm_password')"
                  type="password"
                  autocomplete="new-password"
                />
              </VCol>

              <VCol cols="12">
                <VBtn block type="submit" :loading="loading">
                  {{ $t('Reset Password') }}
                </VBtn>
              </VCol>

              <VCol v-if="errorMessage" cols="12">
                <div class="text-error">
                  {{ errorMessage }}
                </div>
              </VCol>

              <VCol v-if="successMessage" cols="12">
                <div class="text-info">
                  {{ successMessage }}
                </div>
              </VCol>

              <VCol cols="12">
                <RouterLink class="d-flex align-center justify-center" :to="{ name: 'login' }">
                  <VIcon icon="tabler-chevron-left" size="20" class="me-1 flip-in-rtl" />
                  <span>{{ $t('auth.login_link') }}</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";
</style>
